/**
 * Custom hook for smooth progress bar animation
 * Smoothly animates between discrete progress updates (e.g., batch completions)
 */

import { useState, useEffect, useRef } from 'react';

// Animation constants
const UPDATE_INTERVAL_MS = 200;       // 5 updates per second
const BATCH_DURATION_SECONDS = 30;    // Assumed time per batch
const CEILING_BUFFER = 0.01;          // Stop 0.01% before batch boundary
const UPDATES_PER_BATCH = BATCH_DURATION_SECONDS * (1000 / UPDATE_INTERVAL_MS); // 150 updates

/**
 * Hook for smooth progress animation between discrete updates
 *
 * @param {number} actualProgress - The real progress (0-100), updated when batches complete
 * @param {number} totalBatches - Total number of batches (e.g., categories)
 * @param {number} completedBatches - Number of completed batches
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether animation is active (default: true)
 * @returns {number} displayProgress - The smoothly animated progress value (0-100 with decimals)
 */
export function useAnimatedProgress(
  actualProgress,
  totalBatches,
  completedBatches,
  { enabled = true } = {}
) {
  const [displayProgress, setDisplayProgress] = useState(0);
  const intervalRef = useRef(null);
  const stateRef = useRef({
    targetPercent: 0,
    currentPercent: 0,
    incrementPerUpdate: 0,
  });

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // If disabled or no batches, just show actual progress
    if (!enabled || totalBatches === 0) {
      setDisplayProgress(actualProgress);
      return;
    }

    // If complete (100%), show 100
    if (actualProgress >= 100) {
      setDisplayProgress(100);
      return;
    }

    // If starting fresh (actualProgress is 0 and no batches completed), reset display
    if (actualProgress === 0 && completedBatches === 0) {
      setDisplayProgress(0);
    }

    // Calculate batch boundaries
    const percentPerBatch = 100 / totalBatches;
    const nextBatchBoundary = (completedBatches + 1) * percentPerBatch;
    const animationCeiling = Math.min(nextBatchBoundary - CEILING_BUFFER, 99.99);

    // Snap to actual progress if we're behind (batch just completed)
    // Use 0 as starting point if we just reset above
    const currentDisplay = (actualProgress === 0 && completedBatches === 0) ? 0 : displayProgress;
    const startPercent = Math.max(currentDisplay, actualProgress);
    setDisplayProgress(startPercent);

    // Calculate how much room to animate within this batch
    const remainingToAnimate = Math.max(0, animationCeiling - startPercent);
    const incrementPerUpdate = remainingToAnimate > 0
      ? remainingToAnimate / UPDATES_PER_BATCH
      : 0;

    // Update ref for interval callback (avoids stale closure)
    stateRef.current = {
      targetPercent: animationCeiling,
      currentPercent: startPercent,
      incrementPerUpdate,
    };

    // Start animation interval if there's room to animate
    if (incrementPerUpdate > 0) {
      intervalRef.current = setInterval(() => {
        const state = stateRef.current;
        if (state.currentPercent < state.targetPercent) {
          const newPercent = Math.min(
            state.currentPercent + state.incrementPerUpdate,
            state.targetPercent
          );
          state.currentPercent = newPercent;
          setDisplayProgress(newPercent);
        }
      }, UPDATE_INTERVAL_MS);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // Note: displayProgress is intentionally excluded - including it would cause infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualProgress, totalBatches, completedBatches, enabled]);

  // When estimation completes (100%), immediately show 100
  // Note: displayProgress is intentionally not in deps - we only react to actualProgress reaching 100
  useEffect(() => {
    if (actualProgress >= 100) {
      setDisplayProgress(100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualProgress]);

  return displayProgress;
}

export default useAnimatedProgress;
